import { faHomeHeart, faPaperclip } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  QuestInstance,
  Typography,
  UserRole,
  JoonUIColor,
  JoonColorExpanded,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useId } from "react"

import RoutineIcon from "./RoutineIcon"
import { useQuestModalStore } from "./useQuestModalStore"
import { UserInitial } from "./UserInitial"
import Tooltip from "../../../components/tooltip/Tooltip"
import { completeQuestStatuses } from "../../../constants"
import { useFamilyQuery } from "../../../networking/families/queries"
import { useUserQuery } from "../../../networking/user/queries"
import { convertTimeToDatetimeString } from "../../../util/time"
import useCurrentClient from "../hooks/useCurrentClient"

const QuestListItem = ({
  questGroup,
  isFirstInRoutine,
}: {
  questGroup: QuestInstance[]
  isFirstInRoutine: boolean
}) => {
  const { data: family } = useFamilyQuery()
  const { data: user } = useUserQuery()
  const { onOpen: openQuestModal } = useQuestModalStore()
  const { currentClientProfile } = useCurrentClient()

  const quest = questGroup[0]
  const isParentCreated = quest.series.assigner_profile?.role !== UserRole.COACH
  const questUserIds = questGroup.map((quest) => quest.user_id)
  const questUserProfiles =
    family?.profiles.filter((profile) =>
      questUserIds.includes(profile.user.id)
    ) ?? []

  const isAllCompleted = questGroup.every((quest) =>
    completeQuestStatuses.includes(quest.status)
  )

  // Format the parent time for display
  const clientTime = quest.series.reminder_time
  const clientDateTime = convertTimeToDatetimeString(
    clientTime,
    currentClientProfile?.user.timezone
  )
  const coachTime = clientDateTime
    ? dayjs(clientDateTime).tz(user?.timezone!).format("h:mm A")
    : ""

  return (
    <button onClick={() => openQuestModal(questGroup)}>
      <div
        key={quest.id}
        className={`flex flex-row gap-2 py-2 hover:bg-neutral-50 cursor-pointer items-center`}
      >
        <div className="min-w-5 mt-[2px]">
          {isFirstInRoutine && <RoutineIcon routine={quest.series.routine} />}
        </div>
        <Typography
          variant="bodyXSmall"
          textAlign="left"
          className="min-w-[52px] max-w-[52px]"
          color={JoonUIColor.text.secondary}
        >
          {coachTime || "-"}
        </Typography>
        <Typography
          variant="bodyXSmall"
          color={JoonUIColor.text.secondary}
          textAlign="left"
          className="min-w-[52px] max-w-[52px]"
        >
          {clientTime || "-"}
        </Typography>
        <div className="flex flex-row items-center gap-2 justify-between w-full">
          {isParentCreated && <ParentCreatedItemIcon />}
          {quest.series.resource && (
            <FontAwesomeIcon
              icon={faPaperclip}
              fontSize={16}
              className="mt-[2px]"
              color={JoonUIColor.icon.accent}
            />
          )}
          <Typography
            variant="bodySmall"
            color={JoonUIColor.text.secondary}
            textAlign="left"
            className={`${isAllCompleted ? "line-through" : ""}`}
          >
            {quest.series.title}
          </Typography>
        </div>
        <div className="flex flex-row gap-1 pr-1">
          {questUserProfiles.map((profile) => {
            const questStatus = questGroup.find(
              (quest) => quest.user_id === profile.user.id
            )?.status
            const isCompleted =
              !!questStatus && completeQuestStatuses.includes(questStatus)

            return (
              <UserInitial
                key={profile.user.id}
                userProfile={profile}
                isCompleted={isCompleted}
              />
            )
          })}
        </div>
      </div>
    </button>
  )
}

export default QuestListItem

const ParentCreatedItemIcon = () => {
  const id = useId()

  return (
    <>
      <Tooltip id={id}>
        <Typography variant="bodySmall" color={JoonUIColor.text.inverted}>
          Created by parent
        </Typography>
      </Tooltip>
      <div data-tooltip-id={id}>
        <FontAwesomeIcon
          icon={faHomeHeart}
          style={{
            // @ts-ignore
            "--fa-primary-color": JoonUIColor.icon.accent,
            "--fa-secondary-color": JoonColorExpanded.indigo300,
          }}
        />
      </div>
    </>
  )
}
